// Domain formatting for reporting results, shared by the results table, chart,
// and CSV export so headers and values read consistently everywhere.
import { INFECTION_TYPE_LABELS } from '../components/patient/infectionConstants';
import { VISIT_TYPE_LABELS } from '../constants/visitTypeLabels';
import type {
  DatasetColumn,
  DateGranularity,
  GroupBy,
} from '../types/reporting';
import { formatDateToMMDDYYYY, parseISOToLocalDate } from '../utils/dateUtils';

// Money-typed source columns. A grouped measure over one of these (e.g.
// "sum_balance") is money too. This set drives both the compiler's DECIMAL(18,2)
// aggregate cast (isMoneyDatasetColumn) and currency formatting
// (isMoneyColumn), so it is the single source of truth for which columns are
// money.
const MONEY_COLUMNS = new Set([
  'balance',
  'total_charges',
  'paid_amount',
  'adjustment_amount',
  'revenue',
  'expected_payment_amount',
]);

// A money measure alias ("sum_balance") or a pivot value column carrying one
// ("denied_sum_balance") — an aggregate token immediately precedes the money
// column. The required aggregate boundary is what distinguishes a money alias
// from a raw look-alike metric that merely ends in a money word (e.g. a unitless
// "workload_balance"), which must NOT be treated as currency.
const MONEY_MEASURE_ALIAS =
  /(^|_)(sum|avg|min|max)_(balance|total_charges|paid_amount|adjustment_amount|revenue|expected_payment_amount)$/;

const AGG_LABELS: Record<string, string> = {
  sum: 'Sum',
  avg: 'Average',
  min: 'Min',
  max: 'Max',
};

// Aggregate tokens that introduce a measure carrying a column ("sum_balance").
// "count" is handled separately as a lone-token alias. Used to split a pivot
// value column into its value prefix and trailing measure alias.
const AGG_MEASURE_TOKENS = new Set(['sum', 'avg', 'min', 'max']);

const currencyFormatter = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
});
const integerFormatter = new Intl.NumberFormat('en-US', {
  maximumFractionDigits: 0,
});
const decimalFormatter = new Intl.NumberFormat('en-US', {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

export type FormatKind =
  | 'money'
  | 'number'
  | 'date'
  | 'text'
  | 'claim_type'
  | 'infection_type';

// Human-readable labels for the opaque X12 claim-filing-indicator codes stored
// in Claim.claim_type (backend app/models/enum/claim.py ClaimType). Unlike the
// snake_case status enums, these two-character codes sentence-case into garbage
// ("MA" → "Ma"), so billers on the AR/Claims report need the spelled-out payer
// type. Scoped to the claim_type column (see resolveFormatKind) because codes
// like "MA" (Medicare Part A) and "VA" (Veterans Affairs) collide with state
// abbreviations, which must render verbatim.
const CLAIM_TYPE_LABELS: Record<string, string> = {
  '1': 'Other Non-Federal Programs',
  '12': 'Preferred Provider Organization (PPO)',
  '13': 'Point of Service (POS)',
  '14': 'Exclusive Provider Organization (EPO)',
  '15': 'Indemnity Insurance',
  '16': 'HMO Medicare Risk',
  '17': 'Dental Maintenance Organization',
  AM: 'Automobile Medical',
  BL: 'Blue Cross Blue Shield',
  CH: 'CHAMPUS',
  CI: 'Commercial Insurance',
  DS: 'Disability',
  FI: 'Federal Employees Program',
  HM: 'Health Maintenance Organization (HMO)',
  LM: 'Liability Medical',
  MA: 'Medicare Part A',
  MB: 'Medicare Part B',
  MC: 'Medicaid',
  OF: 'Other Federal Program',
  TV: 'Title V',
  VA: 'Veterans Affairs Plan',
  WC: 'Workers Compensation',
  ZZ: 'Mutually Defined',
};

// How a grouped dimension column was bucketed, so cells format by the group's
// granularity rather than the raw source type. Keyed by the group-by column
// name (which is also the result column alias).
export interface DimensionFormat {
  bucket?: boolean; // numeric range bucket → string label ("0-30", "91+")
  dateGranularity?: DateGranularity; // date_trunc bucket → "May 2026", "Q2 2026"
}

/** Build the dimension-format map for a definition's group-bys. */
export function dimensionFormats(
  groupBys: GroupBy[]
): Map<string, DimensionFormat> {
  const map = new Map<string, DimensionFormat>();
  for (const groupBy of groupBys) {
    if (groupBy.transform?.type === 'bucket') {
      map.set(groupBy.column, { bucket: true });
    } else if (groupBy.transform?.type === 'date_trunc') {
      map.set(groupBy.column, { dateGranularity: groupBy.transform.unit });
    }
  }
  return map;
}

// Enum-ish token to humanize (case-insensitive): multi-segment snake_case
// ("not_started", "OASIS_E2_START_OF_CARE_ENHANCED") or a single all-letter
// word ("completed", "MALE"). Excludes identifiers that mix letters and digits
// with no underscore (MRNs like "MR12345", OASIS items like "M1000") and bare
// numbers (ZIP codes), which must render verbatim.
const ENUM_TOKEN = /^([a-z]+|[a-z0-9]+(?:_[a-z0-9]+)+)$/i;

// Clinical acronyms that must stay upper-cased when humanizing a token, rather
// than being lower-cased into "Oasis"/"pt". Compared case-insensitively.
const CLINICAL_ACRONYMS = new Set([
  'OASIS',
  'PT',
  'OT',
  'ST',
  'RN',
  'LPN',
  'LVN',
  'PTA',
  'COTA',
  'HHA',
  'MSW',
  'SN',
  'IV',
  'PRN',
  'CNS',
  'ALF',
  'SNF',
]);

// A word already written as a short, all-caps code is human-readable as-is:
// a state abbreviation ("MA" → Massachusetts) or an X12 payer code that reaches
// the generic humanizer as a pivot value ("MA_count" table header, "MA" chart
// series). Sentence-casing these into "Ma" is the reported mangling, so keep
// them verbatim. Bounded to ≤3 letters so real enum words ("MALE", "UNKNOWN")
// still sentence-case; enum status values arrive lowercased from the backend
// (".value" → "in_review"), so multi-word snake_case tokens are never caught.
const SHORT_CODE = /^[A-Z]{1,3}$/;

function humanizeToken(token: string): string {
  // Prefer the canonical visit-type label for exact enum keys so clinical
  // acronyms and phrasing match the rest of the app ("OASIS Start of Care").
  const canonical = VISIT_TYPE_LABELS[token];
  if (canonical) return canonical;
  // Otherwise sentence-case the token (capitalize the first word, lower-case
  // the rest) while preserving known clinical acronyms and short all-caps codes
  // verbatim.
  return token
    .split('_')
    .map((word, index) => {
      const upper = word.toUpperCase();
      if (CLINICAL_ACRONYMS.has(upper)) return upper;
      if (SHORT_CODE.test(word)) return word;
      const lower = word.toLowerCase();
      return index === 0
        ? lower.charAt(0).toUpperCase() + lower.slice(1)
        : lower;
    })
    .join(' ');
}

const MONTH_NAMES = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];

/** Format a date-bucket value ("2026-05-01") by its group-by granularity. */
function formatDateBucket(
  value: unknown,
  granularity: DateGranularity,
  emptyText: string
): string {
  const date = parseISOToLocalDate(String(value));
  if (!date) return emptyText;
  const year = date.getFullYear();
  switch (granularity) {
    case 'month':
      return `${MONTH_NAMES[date.getMonth()]} ${year}`;
    case 'quarter':
      return `Q${Math.floor(date.getMonth() / 3) + 1} ${year}`;
    case 'week':
      return `Week of ${formatDateToMMDDYYYY(date)}`;
  }
}

/**
 * Exact money membership for a RAW dataset column. The compiler uses this to
 * decide whether to accumulate an aggregate in DECIMAL(18,2): only a real money
 * column qualifies, so a numeric metric that merely ends in a money word
 * ("workload_balance") is never cast or formatted as currency.
 */
export function isMoneyDatasetColumn(name: string): boolean {
  return MONEY_COLUMNS.has(name);
}

/**
 * A RESULT column key that renders as currency: an exact money column
 * ("balance"), a money measure alias ("avg_balance"), or a pivot value column
 * carrying one ("denied_sum_balance"). A money alias/pivot key always has an
 * aggregate token before the money column, so requiring that boundary
 * distinguishes it from a raw look-alike metric ("workload_balance") and from a
 * suffix collision without the "_" boundary ("subtotal_charges").
 */
export function isMoneyColumn(columnKey: string): boolean {
  return MONEY_COLUMNS.has(columnKey) || MONEY_MEASURE_ALIAS.test(columnKey);
}

/**
 * Resolve how a result column should be formatted. Detail/dimension columns are
 * looked up in the manifest; measure aliases ("count", "sum_balance") and pivot
 * value columns aren't in the manifest and are always numeric measures.
 * (Pivot headers join the value and measure with a middle-dot separator; see
 * humanizeHeader.)
 */
export function resolveFormatKind(
  columnKey: string,
  columnMeta?: Map<string, DatasetColumn>,
  dimFormat?: DimensionFormat
): FormatKind {
  // A numeric range bucket outputs string labels ("0-30", "91+"), so treat it
  // as text (left-aligned) instead of the source column's numeric kind.
  if (dimFormat?.bucket) return 'text';
  // The claim_type column holds coded X12 payer types; format it through the
  // display map rather than the generic text humanizer.
  if (columnKey === 'claim_type') return 'claim_type';
  // The infection_type column holds infection-log enum values; format it through
  // the Events-tab label map so the wording matches ("covid_19" → "COVID-19").
  if (columnKey === 'infection_type') return 'infection_type';
  const meta = columnMeta?.get(columnKey);
  if (meta) {
    if (meta.type === 'date') return 'date';
    if (meta.type === 'number') {
      return isMoneyColumn(columnKey) ? 'money' : 'number';
    }
    return 'text';
  }
  return isMoneyColumn(columnKey) ? 'money' : 'number';
}

/** Numeric kinds are right-aligned in tables. */
export function isNumericKind(kind: FormatKind): boolean {
  return kind === 'money' || kind === 'number';
}

/**
 * Format a single cell value for the given format kind. `dateGranularity`
 * formats date-bucket cells by their group-by granularity; `emptyText` sets the
 * null/blank placeholder (em-dash in the UI, empty string for CSV).
 */
export function formatValue(
  value: unknown,
  kind: FormatKind,
  options?: { dateGranularity?: DateGranularity; emptyText?: string }
): string {
  const emptyText = options?.emptyText ?? '—';
  if (value === null || value === undefined || value === '') return emptyText;
  switch (kind) {
    case 'money':
      return typeof value === 'number'
        ? currencyFormatter.format(value)
        : String(value);
    case 'number':
      if (typeof value === 'number') {
        return Number.isInteger(value)
          ? integerFormatter.format(value)
          : decimalFormatter.format(value);
      }
      return String(value);
    case 'date':
      if (options?.dateGranularity) {
        return formatDateBucket(value, options.dateGranularity, emptyText);
      }
      return formatDateToMMDDYYYY(value as string) || emptyText;
    case 'claim_type': {
      // Known code → readable payer type; fall back to the generic humanizer
      // (then verbatim) for any code not in the map.
      const text = String(value);
      return (
        CLAIM_TYPE_LABELS[text] ??
        (ENUM_TOKEN.test(text) ? humanizeToken(text) : text)
      );
    }
    case 'infection_type': {
      // Known infection enum → its Events-tab label; fall back to the generic
      // humanizer (then verbatim) for any value not in the map.
      const text = String(value);
      return (
        INFECTION_TYPE_LABELS[text] ??
        (ENUM_TOKEN.test(text) ? humanizeToken(text) : text)
      );
    }
    case 'text':
    default: {
      const text = String(value);
      return ENUM_TOKEN.test(text) ? humanizeToken(text) : text;
    }
  }
}

/**
 * Split a pivot value column ("MA_count", "denied_sum_total_charges") into its
 * pivot value prefix and trailing measure alias. Mirrors chartFormat.splitMeasure
 * but lives here so header humanizing can map the prefix through the pivot
 * column's display formatter. Scans right-to-left so a pivot value that itself
 * contains an agg-like word still yields the trailing measure; returns an empty
 * prefix when no measure token is found (a plain grouped measure alias).
 */
function splitPivotKey(key: string): { prefix: string; alias: string } {
  if (key === 'count') return { prefix: '', alias: 'count' };
  const tokens = key.split('_');
  for (let i = tokens.length - 1; i >= 0; i--) {
    const token = tokens[i];
    if (token === 'count' && i === tokens.length - 1) {
      return { prefix: tokens.slice(0, i).join('_'), alias: 'count' };
    }
    if (AGG_MEASURE_TOKENS.has(token) && i < tokens.length - 1) {
      return {
        prefix: tokens.slice(0, i).join('_'),
        alias: tokens.slice(i).join('_'),
      };
    }
  }
  return { prefix: '', alias: key };
}

/**
 * Humanize a pivot value token using the pivot column's display formatting, so a
 * pivot on claim_type renders "MA" as "Medicare Part A" (not "Ma") and a pivot on
 * an enum status renders "not_started" as "Not started". Falls back to the
 * generic token humanizer when the pivot column is unknown.
 */
export function humanizePivotValue(
  value: string,
  pivotColumn?: string,
  columnMeta?: Map<string, DatasetColumn>
): string {
  if (pivotColumn) {
    const kind = resolveFormatKind(pivotColumn, columnMeta);
    // A pivot bucket is a discrete category label. Only coded/date columns need
    // their own formatter (claim_type → payer label, infection_type → clinical
    // label, date_trunc → "May 2026");
    // every other bucket — including a text column missing from the manifest,
    // which resolveFormatKind would otherwise default to 'number' and pass through
    // raw — humanizes as an enum token below (so "denied" → "Denied").
    if (kind === 'claim_type' || kind === 'infection_type' || kind === 'date') {
      return formatValue(value, kind, { emptyText: value });
    }
  }
  return ENUM_TOKEN.test(value) ? humanizeToken(value) : value;
}

/**
 * Readable measure label for a pivot value header: "count" → "Count",
 * "sum_total_charges" → "Total charges (sum)". The parenthetical aggregate keeps
 * the value and measure grammatically separable ("Denied · Total charges (sum)")
 * instead of the ambiguous "Denied sum total charges".
 */
function measureLabelParen(
  alias: string,
  columnLabels?: Map<string, string>,
  columnMeta?: Map<string, DatasetColumn>
): string {
  if (alias === 'count') return 'Count';
  const underscore = alias.indexOf('_');
  const agg = alias.slice(0, underscore);
  const base = alias.slice(underscore + 1);
  if (agg in AGG_LABELS) {
    const baseLabel =
      columnLabels?.get(base) ??
      columnMeta?.get(base)?.label ??
      humanizeToken(base);
    return `${baseLabel} (${AGG_LABELS[agg].toLowerCase()})`;
  }
  return humanizeToken(alias);
}

/**
 * Human-friendly header for a result column: manifest label when known, else a
 * humanized measure alias ("count" → "Count", "sum_balance" → "Sum of
 * Balance") or a humanized token (pivot enum values like "not_started"). When a
 * `pivot` column is supplied, a pivot value column is split into its value and
 * measure and rendered as "<value> · <measure>", mapping the value through the
 * pivot column's display formatter (so claim_type codes read "Medicare Part A").
 */
export function humanizeHeader(
  columnKey: string,
  columnLabels?: Map<string, string>,
  columnMeta?: Map<string, DatasetColumn>,
  pivot?: { column: string }
): string {
  const label = columnLabels?.get(columnKey);
  if (label) return label;
  if (pivot) {
    const { prefix, alias } = splitPivotKey(columnKey);
    if (prefix) {
      const valueLabel = humanizePivotValue(prefix, pivot.column, columnMeta);
      const measure = measureLabelParen(alias, columnLabels, columnMeta);
      return `${valueLabel} · ${measure}`;
    }
  }
  if (columnKey === 'count') return 'Count';
  const underscore = columnKey.indexOf('_');
  if (underscore > 0) {
    const agg = columnKey.slice(0, underscore);
    const base = columnKey.slice(underscore + 1);
    if (agg in AGG_LABELS) {
      const baseLabel =
        columnLabels?.get(base) ??
        columnMeta?.get(base)?.label ??
        humanizeToken(base);
      return `${AGG_LABELS[agg]} of ${baseLabel}`;
    }
  }
  return humanizeToken(columnKey);
}
